import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { useClient } from 'src/clients/hooks/useClient';
import { useClients } from 'src/clients/hooks/useClients';
import { useFamily } from 'src/clients/hooks/useFamily';
import { useFamilyRoles } from 'src/clients/hooks/useFamilyRoles';
import { getFirstClientParticipant } from 'src/meetings/utils/event-utils';
import type { AddParticipantBody } from 'src/meetings/hooks/useMeetingParticipants';
import type { FamilyMeeting } from 'src/meetings/hooks/useScopedMeetings';
import type { MeetingScope } from 'src/meetings/scope';
import { useProfessionalConnections } from './useProfessionalConnections';

export type AttendeeGroup = 'adviser' | 'guest' | 'keyContact';

export type Attendee = {
  id: string;
  name: string;
  role: string;
  group: AttendeeGroup;
  participantId: string | null;
  defaultChecked: boolean;
  addBody: AddParticipantBody;
  isExternalGuest: boolean;
};

/**
 * Builds the attendee list shown in the meeting member-selection modals:
 * the advisor, every member of the meeting's family, plus anyone already on the
 * meeting who isn't one of those. For an individual scope the family is resolved
 * from the client so the same roster is offered as in the family view; a client
 * with no family simply surfaces via the existing-participant pass.
 * `defaultChecked` reflects who is currently on the meeting.
 */
export function useMeetingAttendees(scope: MeetingScope, meeting: FamilyMeeting): Attendee[] {
  // The hub carries no familyId in the scope, so resolve the meeting's family from
  // a client participant — otherwise only the members already on the meeting show
  // instead of the whole household.
  const { client: leadClient } = useClient(leadClientId(scope, meeting));
  const familyId = scope.kind === 'family' ? scope.familyId : (leadClient?.familyId ?? undefined);
  const { family } = useFamily(familyId);
  const { clients } = useClients({ familyId }, { enabled: !!familyId });
  const { familyRoles } = useFamilyRoles();
  const { currentUser } = useCurrentUser();
  const connections = useProfessionalConnections(scope);

  const connectionNames = new Set(
    connections.map(connection => connection.name.trim().toLowerCase()),
  );

  return buildAttendees({
    family,
    clients,
    familyRoles,
    currentUser,
    meeting,
    connectionNames,
  });
}

type Family = NonNullable<ReturnType<typeof useFamily>['family']>;
type Client = ReturnType<typeof useClients>['clients'][number];
type FamilyRole = ReturnType<typeof useFamilyRoles>['familyRoles'][number];
type CurrentUser = ReturnType<typeof useCurrentUser>['currentUser'];
type MeetingParticipant = FamilyMeeting['eventParticipants'][number]['participant'];

function buildAttendees({
  family,
  clients,
  familyRoles,
  currentUser,
  meeting,
  connectionNames,
}: {
  family: Family | null;
  clients: Client[];
  familyRoles: FamilyRole[];
  currentUser: CurrentUser;
  meeting: FamilyMeeting;
  connectionNames: Set<string>;
}): Attendee[] {
  const clientById = Object.fromEntries(clients.map(client => [client.id, client]));
  const roleNameById = Object.fromEntries(familyRoles.map(role => [role.id, role.name]));

  const participantByKey = new Map<
    string,
    { participantId: string | null; participant: MeetingParticipant }
  >();
  for (const eventParticipant of meeting.eventParticipants) {
    const participant = eventParticipant.participant;
    participantByKey.set(participantKey(participant), {
      participantId: participant.id,
      participant,
    });
  }

  const attendees: Attendee[] = [];
  const representedKeys = new Set<string>();

  if (currentUser) {
    const key = `user:${currentUser.id}`;
    representedKeys.add(key);
    attendees.push({
      id: key,
      name: `${currentUser.firstName ?? ''} (You)`.trim(),
      role: 'Adviser',
      group: 'adviser',
      participantId: participantByKey.get(key)?.participantId ?? null,
      defaultChecked: participantByKey.has(key),
      addBody: { participantType: 'USER', userId: currentUser.id },
      isExternalGuest: false,
    });
  }

  for (const clientId of familyMemberClientIds(family)) {
    const client = clientById[clientId];
    if (!client) continue;
    const key = `client:${clientId}`;
    representedKeys.add(key);
    attendees.push({
      id: key,
      name: `${client.firstName} ${client.lastName}`.trim(),
      role: familyMemberRole(family, roleNameById, clientId) ?? 'Family member',
      group: 'guest',
      participantId: participantByKey.get(key)?.participantId ?? null,
      defaultChecked: participantByKey.has(key),
      addBody: { participantType: 'CLIENT', clientId },
      isExternalGuest: false,
    });
  }

  // Anyone already on the meeting who isn't the advisor or a scoped client —
  // e.g. a client added via "Choose a client", or an external attendee.
  for (const [key, { participantId, participant }] of participantByKey) {
    if (representedKeys.has(key)) continue;
    attendees.push({
      id: key,
      participantId,
      defaultChecked: true,
      ...extraAttendeeFields(participant, family, roleNameById, connectionNames),
    });
  }

  return attendees;
}

function leadClientId(scope: MeetingScope, meeting: FamilyMeeting): string | undefined {
  if (scope.kind === 'individual') return scope.clientId;
  if (scope.kind === 'all') return getFirstClientParticipant(meeting)?.participant.client?.id;
  return undefined;
}

function familyMemberClientIds(family: Family | null): string[] {
  const seen = new Set<string>();
  return [
    family?.mainFamilyMember.clientId,
    ...(family?.secondaryFamilyMembers ?? []).map(s => s.clientId),
    ...(family?.members ?? []).map(m => m.clientId),
  ].filter((id): id is string => !!id && !seen.has(id) && (seen.add(id), true));
}

function familyMemberRole(
  family: Family | null,
  roleNameById: Record<string, string>,
  clientId: string,
): string | null {
  if (!family) return null;

  // Role model: the role lives on the members entry. In the star model that same
  // entry is a role-less peer edge, so only treat it as authoritative when it
  // actually carries a role id.
  const member = family.members.find(m => m.clientId === clientId);
  if (member?.familyRoleId && roleNameById[member.familyRoleId]) {
    return roleNameById[member.familyRoleId];
  }

  if (family.mainFamilyMember.clientId === clientId) {
    const roleId = family.mainFamilyMember.familyRoleId;
    if (roleId && roleNameById[roleId]) return roleNameById[roleId];
  }

  // Star model: the label is the relationship to the main family member.
  const secondary = family.secondaryFamilyMembers.find(s => s.clientId === clientId);
  if (secondary?.relationshipToMainFamilyMember) {
    return secondary.relationshipToMainFamilyMember;
  }

  const inFamily =
    family.mainFamilyMember.clientId === clientId || Boolean(member) || Boolean(secondary);
  return inFamily ? 'Family member' : null;
}

function participantKey(participant: MeetingParticipant): string {
  if (participant.client?.id) return `client:${participant.client.id}`;
  if (participant.user?.id) return `user:${participant.user.id}`;
  return `participant:${participant.id ?? ''}`;
}

function extraAttendeeFields(
  participant: MeetingParticipant,
  family: Family | null,
  roleNameById: Record<string, string>,
  connectionNames: Set<string>,
): {
  name: string;
  role: string;
  group: AttendeeGroup;
  addBody: AddParticipantBody;
  isExternalGuest: boolean;
} {
  if (participant.client?.id) {
    return {
      name: `${participant.client.firstName} ${participant.client.lastName}`.trim(),
      role: familyMemberRole(family, roleNameById, participant.client.id) ?? 'Client',
      group: 'guest',
      addBody: { participantType: 'CLIENT', clientId: participant.client.id },
      isExternalGuest: false,
    };
  }
  if (participant.user?.id) {
    return {
      name: `${participant.user.firstName ?? ''} ${participant.user.lastName ?? ''}`.trim(),
      role: 'Adviser',
      group: 'adviser',
      addBody: { participantType: 'USER', userId: participant.user.id },
      isExternalGuest: false,
    };
  }
  const name = participant.name ?? 'Guest';
  const isKeyContact = connectionNames.has(name.trim().toLowerCase());
  return {
    name,
    role: participant.description ?? 'Guest',
    group: isKeyContact ? 'keyContact' : 'guest',
    addBody: {
      participantType: 'EXTERNAL',
      name: participant.name ?? '',
      description: participant.description ?? '',
    },
    isExternalGuest: !isKeyContact,
  };
}
